import axios from 'axios'
import { getClientFingerprint } from '../utils/fingerprint.util'
import { refreshSession as sharedRefreshSession } from '../utils/sessionToken.util'
import { CAP_CLEARED_EVENT, createCapSuspendedGate } from '../utils/daily-limit-event.util'

const currentPath = window.location.hostname;
const baseURL = (currentPath?.split('.')?.includes('lms2') ? process.env.REACT_APP_API_2_URL : process.env.REACT_APP_API_URL);

// KHÔNG set Authorization header tĩnh ở module-level vì:
//   1. localStorage.getItem("user-jwt-tk") chỉ được đọc 1 lần khi module
//      load → token mới sau login không có hiệu lực cho tới khi reload.
//   2. Khi `user-jwt-tk` không tồn tại (lms-frontend dùng `sessionToken`/
//      `moodleToken` thay vì legacy `user-jwt-tk`), header sẽ là chuỗi
//      `Bearer null` — vô nghĩa và có thể bị Cloudflare/proxy reject.
// Authorization được inject dynamically trong request interceptor bên
// dưới (đọc fresh mỗi request, ưu tiên sessionToken trước user-jwt-tk).
const axiosClient = axios.create({ baseURL })

const handleResponse = res => {
  // Sliding TTL: backend reissues sessionToken when ≤30 min remain and
  // ships the fresh JWT in this header. Persist immediately so the next
  // request (this tab + cross-tab via storage event) uses the new token
  // before the old one expires. Old token stays valid until its real exp,
  // so concurrent in-flight requests are safe.
  const refreshed = res?.headers?.['x-session-token-refreshed'];
  if (refreshed) {
    try { localStorage.setItem('sessionToken', refreshed); } catch (_) { /* noop */ }
  }
  return res.data
}

// 426 Upgrade Required + code=CLIENT_OUTDATED → backend báo client đang chạy
// bundle cũ không tương thích contract mới. Show toast 1 lần (idempotent qua
// flag global) rồi reload trang với cache-busting query để lấy bundle mới.
let _outdatedReloadFired = false;

const handleClientOutdated = (err) => {
  if (_outdatedReloadFired) return;
  if (err?.response?.status !== 426) return;
  if (err?.response?.data?.code !== 'CLIENT_OUTDATED') return;
  _outdatedReloadFired = true;

  const msg = err.response.data.message
    || 'Phiên bản cũ. Đang tải lại trang để cập nhật...';

  // Hiển thị message bằng alert thuần (axios interceptor không nên import UI lib).
  // Components có thể catch riêng error này nếu muốn UX khác.
  try { alert(msg); } catch (_) { /* SSR-safe noop */ }

  setTimeout(() => {
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('_v', String(Date.now()));
      window.location.href = url.toString();
    } catch (_) {
      window.location.reload();
    }
  }, 500);
};

// 401 trên endpoint sessionAuth-guarded — 2 chế độ tuỳ thuộc BE config:
//
//   A. FULL ROTATION MODE (BE có ELEARNING_REFRESH_TOKEN_SECRET, FE có
//      refreshToken trong localStorage):
//      - 401 → POST /elearning/refresh → nhận pair mới → retry request
//        gốc với sessionToken mới (transparent cho user).
//      - Concurrent 401s xếp hàng trong `pendingRefresh` để 100 request
//        đồng thời chỉ trigger 1 refresh.
//      - Refresh fail (replay/expired/revoked) → silent bounce login
//        với toast warn (KHÔNG alert blocking).
//
//   B. GRACEFUL DEGRADE MODE (BE không có refresh secret, FE không có
//      refreshToken):
//      - Không có way để refresh → KHÔNG thử refresh + KHÔNG bounce.
//      - Để 401 propagate đến caller như behavior trước khi refresh
//        rotation được introduce. Page bị broken UI vẫn tốt hơn alert
//        spam, vì:
//          * User vừa login fresh có thể gặp 401 race (stale request
//            từ pre-login) → bounce sẽ vô hạn vòng lặp login.
//          * BE thiếu env nhưng login vẫn cấp sessionToken được → đa
//            số request OK, chỉ 1 vài endpoint stale thì fail.
//
// Switch giữa A và B dựa trên `localStorage.refreshToken` exists hay
// không. Login flow là điểm duy nhất set/clear refreshToken.
// Refresh logic moved to shared util (sessionToken.util) so HTTP 401 và
// socket connect_error CHIA SẺ cùng 1 pending promise → khi cả hai cùng
// gặp token hết hạn, chỉ 1 request /elearning/refresh được gửi, và token
// mới được cả hai dùng chung. Tránh xung đột axios-refresh vs socket-logout.
const refreshSessionToken = sharedRefreshSession;

let _bouncedAlready = false;
const bounceToLogin = () => {
  if (_bouncedAlready) return;
  _bouncedAlready = true;
  try { localStorage.removeItem('sessionToken'); } catch (_) { /* noop */ }
  try { localStorage.removeItem('refreshToken'); } catch (_) { /* noop */ }
  try { localStorage.removeItem('user-jwt-tk'); } catch (_) { /* noop */ }
  try { localStorage.removeItem('user-jwt-rftk'); } catch (_) { /* noop */ }
  // Dynamic import để khỏi tạo circular dep với toastify ở module init.
  import('react-toastify').then(({ toast }) => {
    try {
      toast.warn('Phiên đăng nhập đã hết hạn. Đang chuyển về trang đăng nhập...', { autoClose: 2500 });
    } catch (_) { /* noop */ }
  }).catch(() => { /* noop */ });
  setTimeout(() => {
    try { window.location.href = '/elearning/login'; } catch (_) { /* noop */ }
  }, 1500);
};

const handleSessionExpired = async (err) => {
  if (err?.response?.status !== 401) return null;
  const hasSession = !!localStorage.getItem('sessionToken')
    || !!localStorage.getItem('user-jwt-tk');
  if (!hasSession) return null;
  const url = err?.config?.url || '';
  if (/\/elearning\/(login|refresh)(\?|$)/.test(url)) return null;

  // Không có refreshToken (BE chưa cấu hình rotation secret) → không refresh
  // được → LOGOUT NGAY (không chờ reload). Trước đây nhánh này `return null`
  // (không bounce): 401 chỉ propagate xuống component, token hết hạn vẫn nằm
  // trong localStorage, mà guard chỉ check token CÓ MẶT (không check exp) →
  // reload cũng KHÔNG logout → HV kẹt trang vỡ vô thời hạn. Giờ bounce thẳng.
  // bounceToLogin có `_bouncedAlready` (chỉ fire 1 lần/phiên) + đã loại trừ
  // route login/refresh ở trên → không vòng lặp login.
  const hasRefreshToken = !!localStorage.getItem('refreshToken');
  if (!hasRefreshToken) {
    bounceToLogin();
    return null;
  }

  // Đã retry 1 lần rồi mà vẫn 401 → refresh không cứu được, bounce.
  if (err?.config?._retriedAfterRefresh) {
    bounceToLogin();
    return null;
  }

  try {
    // sharedRefreshSession tự gom concurrent caller vào 1 pending promise.
    const newSessionToken = await refreshSessionToken();

    const retryConfig = { ...err.config };
    retryConfig.headers = { ...(retryConfig.headers || {}), Authorization: `Bearer ${newSessionToken}` };
    retryConfig._retriedAfterRefresh = true;
    return axiosClient(retryConfig);
  } catch (refreshErr) {
    bounceToLogin();
    return null;
  }
};

// 403 + code=CAP_SUSPENDED → BE đã khoá tài khoản HV (vượt cap 8h/ngày
// hoặc admin manual). Show alert 1 lần, không auto-reload (HV cần đọc).
// Component vẫn nhận lại error để pause auto-save loop / disable UI.
// Chống lặp theo thời gian (không phải 1 lần/tab): HV được mở khoá rồi bị
// khoá lại trong cùng tab vẫn thấy overlay.
// Overlay đóng ("Để sau" / sang ngày) → mở lại cổng: lần khoá kế tiếp báo ngay.
// Trang sách và video tự dừng gửi sau 1 lượt 403 nên không bị bắn liên tục.
const CAP_SUSPENDED_REFIRE_MS = 5 * 60 * 1000;
const capSuspendedGate = createCapSuspendedGate(CAP_SUSPENDED_REFIRE_MS);
try {
  window.addEventListener(CAP_CLEARED_EVENT, () => capSuspendedGate.reset());
} catch (_) { /* SSR-safe */ }

const handleCapSuspended = (err) => {
  if (err?.response?.status !== 403) return;
  if (err?.response?.data?.code !== 'CAP_SUSPENDED') return;
  if (!capSuspendedGate.shouldFire()) return;

  const msg = err.response.data.message
    || 'Tài khoản đang bị tạm khoá do vượt cap giờ học/ngày. Liên hệ trung tâm để được mở khoá.';

  // Phát global event để pages chủ động (vd ElearningStudentBookPage)
  // pause auto-save timers + show modal contextual. Alert backup nếu
  // không có listener.
  try {
    window.dispatchEvent(new CustomEvent('cap-suspended', {
      detail: {
        message: msg,
        capSuspendedAt: err.response.data.capSuspendedAt,
        resetAt: err.response.data.resetAt,
        permanent: !!err.response.data.permanent,
      },
    }));
  } catch (_) { /* SSR-safe */ }

  // Backup alert sau 200ms — nếu component đã handle event, có thể
  // call event.preventDefault() (nhưng axios ko cancel — đây là delay
  // để component mount listener kịp hiện modal đẹp).
  setTimeout(() => {
    if (!window.__capSuspendedHandled) {
      try { alert(msg); } catch (_) { /* noop */ }
    }
  }, 200);
};

// 428 + code=password_change_required → BE detect Moodle force-password-change
// (vd login lần đầu, admin reset password). FE sync localStorage flag để
// StudentElearningGuard redirect sang ForceChangePasswordPage. KHÔNG dùng
// alert + reload — chuyển hướng trực tiếp để user thấy form đổi mật khẩu
// ngay, không trải qua nhiều màn lỗi.
let _pwdChangeFired = false;
const handlePasswordChangeRequired = (err) => {
  if (_pwdChangeFired) return;
  if (err?.response?.status !== 428) return;
  if (err?.response?.data?.code !== 'password_change_required') return;
  _pwdChangeFired = true;

  try { localStorage.setItem('forcePasswordChange', '1'); } catch (_) { /* noop */ }
  // Tránh loop nếu user đang Ở chính trang change-password rồi.
  if (window.location.pathname.includes('/elearning/change-password')) return;
  setTimeout(() => {
    try { window.location.href = '/elearning/change-password'; } catch (_) { /* noop */ }
  }, 200);
};

const handleError = async err => {
  handleClientOutdated(err);
  handleCapSuspended(err);
  handlePasswordChangeRequired(err);
  // handleSessionExpired có thể return Promise resolve-with-retry-result
  // (request retry sau refresh thành công). Nếu null → tiếp tục reject
  // như cũ. Phải await để không break caller .then() chain.
  const retryResult = await handleSessionExpired(err);
  if (retryResult !== null && retryResult !== undefined) {
    return retryResult;
  }
  return Promise.reject(err);
};

// Build version để backend đối chiếu với MIN_CLIENT_VERSION; backend
// trả 426 nếu version này < min → handleClientOutdated reload trang.
// Nguồn: REACT_APP_CLIENT_VERSION inject lúc build (CI set thành
// timestamp build hoặc git SHA). Fallback chuỗi rỗng → backend treat
// như không có header → skip check (an toàn cho dev / build chưa set).
const CLIENT_VERSION = process.env.REACT_APP_CLIENT_VERSION || '';

axiosClient.interceptors.request.use(
  req => {
    // Authorization: ưu tiên sessionToken (JWT mới sau khi HV login qua
    // elearning session), fallback về user-jwt-tk (legacy OTP login).
    // Đọc fresh mỗi request để tôn trọng login/logout. Nếu cả 2 đều
    // không có (vd HV chưa login + endpoint public như /devtools-event),
    // KHÔNG set header — tránh gửi `Bearer null` gây 401 ở proxy.
    const sessionToken = localStorage.getItem('sessionToken')
    const legacyToken = localStorage.getItem('user-jwt-tk')
    const authToken = sessionToken || legacyToken
    if (authToken) {
      req.headers['Authorization'] = `Bearer ${authToken}`
    }

    if (CLIENT_VERSION) {
      req.headers['X-Client-Version'] = CLIENT_VERSION;
    }

    // Phase 2D step 5: dropped `X-Moodle-Token` forwarding. Every BE
    // endpoint that used to read it now resolves the student via the
    // sessionToken Bearer above (with X-Moodle-Token kept on the server
    // side as a transition fallback for un-updated FE tabs). Removing
    // the header here is what makes the moodleToken stop traveling on
    // every BE request.

    // Anti-cheat correlation header. Backend logs fingerprint drift
    // within a single quiz attempt. Computed once per tab via
    // sessionStorage, so the header value is cheap to attach.
    try {
      req.headers['X-Client-Fingerprint'] = getClientFingerprint()
    } catch (_) { /* SSR-safe noop */ }

    // Session-ownership: BE guard (requireSessionOwnership) đối chiếu tab
    // gửi request ghi tiến độ (video save, quiz save/finish) với tab đang
    // giữ quyền học trong Redis. Cùng sessionStorage key 'elearning-tab-id'
    // với socket handshake (hooks/useElearningSession getTabId) — tạo nếu
    // chưa có để header luôn hiện diện.
    try {
      let tabId = sessionStorage.getItem('elearning-tab-id')
      if (!tabId) {
        tabId = crypto.randomUUID()
        sessionStorage.setItem('elearning-tab-id', tabId)
      }
      req.headers['X-Elearning-Tab-Id'] = tabId
    } catch (_) { /* SSR-safe noop */ }

    return req
  },
  err => {
    return Promise.reject(err)
  }
)

axiosClient.interceptors.response.use(handleResponse, handleError)

export default axiosClient
